import { localDayKey } from "./days";
import { clockOf, dayLabel, localInstant, localParts } from "./reminders";
import { shortDate } from "./weekLine";

/* SCHEDULED CALLS — the web's pure rules (his idea 2026-10-10, `docs/CALLS-BRIEF.md`, session 1 of 4). The phone's twin is
 * `HybridTrainer/Services/CoachCalls.swift` (`CoachCallRules`); gate `calls-are-booked-and-rung` holds the lengths, the
 * topic cap, the join lead and the chat-line heads equal across the two. The table is `coach_calls` (`20261012100000`).
 *
 * The WEB COACH books a call from a client's page (day, time, 15–60 min, an optional topic), sees the next calls on the
 * Dashboard (`Sam Carter · today 18:00`), and moves or cancels one; each change leaves ONE line in the chat. The web has no
 * notifications — the reminders are the phones' (`CoachCallStore`). */

/** 🔴 THE ONE SWITCH (the `COMMUNITY_INCLUDED` shape; the phone's twin `CallsFeature.isIncluded`). OFF in the deployed site
 *  until session 3 brings the real call — a `Join` that opens nothing is a promise the page cannot keep. ON under
 *  `npm run dev`, so the pages render locally. Session 3 turns both on in the same commit. */
export const CALLS_INCLUDED: boolean = import.meta.env.DEV;

export const CALL_LENGTHS = [15, 30, 45, 60] as const;
export const CALL_DEFAULT_LENGTH = 30;
export const CALL_TOPIC_MAX = 80;
/** `Join` opens 10 minutes before the start and stays until the end. */
export const CALL_JOIN_LEAD_MS = 10 * 60_000;

export type Call = {
  id: string; coachId: string; clientId: string;
  startsAt: string; minutes: number; topic: string | null; cancelledAt: string | null;
};
export type CallRow = {
  id: string; coach_id: string; client_id: string; starts_at: string; minutes: number; topic: string | null; cancelled_at: string | null;
};
export const CALL_COLUMNS = "id, coach_id, client_id, starts_at, minutes, topic, cancelled_at";

export function fromCallRow(r: CallRow): Call {
  return { id: r.id, coachId: r.coach_id, clientId: r.client_id, startsAt: r.starts_at, minutes: r.minutes,
           topic: r.topic, cancelledAt: r.cancelled_at };
}

export function callEnds(c: Call): number { return Date.parse(c.startsAt) + c.minutes * 60_000; }

export function canJoin(c: Call, now: Date): boolean {
  const t = now.getTime();
  return c.cancelledAt === null && t >= Date.parse(c.startsAt) - CALL_JOIN_LEAD_MS && t < callEnds(c);
}

/** Not cancelled and not over, nearest first. */
export function upcomingCalls(calls: readonly Call[], now: Date): Call[] {
  return calls.filter((c) => c.cancelledAt === null && callEnds(c) > now.getTime())
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt) || a.id.localeCompare(b.id));
}

/** The Dashboard's card: the coach's live calls in the next seven days. */
export function dashboardCalls(calls: readonly Call[], coachId: string, now: Date): Call[] {
  const week = now.getTime() + 7 * 86_400_000;
  return upcomingCalls(calls, now).filter((c) => c.coachId === coachId && Date.parse(c.startsAt) < week);
}

/** `today 18:00` · `tomorrow 18:00` · `Tue 14 Oct, 18:00` — the phone's `CoachCallRules.when`, on the coach's clock. */
export function callWhen(iso: string, now: Date, zone: string): string {
  const day = localDayKey(new Date(iso), zone);
  const label = dayLabel(day, now, zone);
  const time = clockOf(iso, zone);
  if (label === "Today") return `today ${time}`;
  if (label === "Tomorrow") return `tomorrow ${time}`;
  return `${shortDate(day)}, ${time}`;
}

/** The written date of a chat line — always the full date (`Tue 14 Oct, 18:00`), because the line is read later. */
export function callStamp(iso: string, zone: string): string {
  return `${shortDate(localDayKey(new Date(iso), zone))}, ${clockOf(iso, zone)}`;
}

/** The three changes the chat records — the phone's `CoachCallRules.head` says the same words (the gate holds them equal). */
export const CALL_LINE_HEADS = { booked: "Call booked", moved: "Call moved to", cancelled: "Call cancelled" } as const;
export type CallChange = keyof typeof CALL_LINE_HEADS;

/** ONE chat line per change: `Call booked · Tue 14 Oct, 18:00 · 30 min · Squat depth`, `Call moved to Wed 15 Oct, 18:00`,
 *  `Call cancelled · Tue 14 Oct, 18:00`. The date is the writer's clock (the line is text, stored once). */
export function callLine(change: CallChange, c: Pick<Call, "startsAt" | "minutes" | "topic">, zone: string): string {
  const when = callStamp(c.startsAt, zone);
  if (change === "booked") return [CALL_LINE_HEADS.booked, when, `${c.minutes} min`, c.topic].filter((p): p is string => p !== null && p !== "").join(" · ");
  if (change === "moved") return `${CALL_LINE_HEADS.moved} ${when}`;
  return `${CALL_LINE_HEADS.cancelled} · ${when}`;
}

/* ── The draft (the call window) ─────────────────────────────────────────────────────────────────────────── */

export type CallDraft = { day: string; time: string; minutes: number; topic: string };

/** A new call's default: a future day → 18:00 that day; today → the next whole hour at least an hour ahead. */
export function defaultCallDraft(day: string | null, now: Date, zone: string): CallDraft {
  const here = localParts(now.toISOString(), zone);
  if (day !== null && day > here.day) return { day, time: "18:00", minutes: CALL_DEFAULT_LENGTH, topic: "" };
  const ahead = localParts(new Date(now.getTime() + 2 * 3_600_000).toISOString(), zone);
  return { day: ahead.day, time: `${ahead.time.slice(0, 2)}:00`, minutes: CALL_DEFAULT_LENGTH, topic: "" };
}

export function draftFromCall(c: Call, zone: string): CallDraft {
  const p = localParts(c.startsAt, zone);
  return { day: p.day, time: p.time, minutes: c.minutes, topic: c.topic ?? "" };
}

/** What stops a draft saving, in words — or null. The table refuses the same; this says it first. */
export function callDraftFault(d: CallDraft, now: Date, zone: string): string | null {
  const at = localInstant(d.day, d.time, zone);
  if (at === null) return "Choose a day and a time.";
  if (Date.parse(at) < now.getTime()) return "Pick a time that has not passed.";
  if (!(CALL_LENGTHS as readonly number[]).includes(d.minutes)) return "Pick 15, 30, 45 or 60 minutes.";
  if (d.topic.trim().length > CALL_TOPIC_MAX) return `A topic is ${CALL_TOPIC_MAX} characters at most.`;
  return null;
}

/** The columns a draft writes (book and the coach's move) — the ONE shape. Null when the draft is at fault. */
export function callDraftRow(d: CallDraft, now: Date, zone: string): { starts_at: string; minutes: number; topic: string | null } | null {
  if (callDraftFault(d, now, zone) !== null) return null;
  const t = d.topic.trim();
  return { starts_at: localInstant(d.day, d.time, zone) as string, minutes: d.minutes, topic: t === "" ? null : t };
}
